import _ from 'lodash';
import moment from 'moment';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import { useTranslator } from '@jutro/locale';
import { WniDateUtil, WniProductsUtil, WniProductsDateUtil, ConfigUtil } from 'wni-portals-util-js';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { AOConfig } from 'wni-portals-config-js';


const {
    aoStates,
    companyGroups,
    statesForGroup1,
    statesForGroup2,
    statesForGroup3,
    statesForGroup4
} = AOConfig;

const  AVAILABLE_CODE = 'Available';
const UNAVAILABLE_CODE = 'Unavailable';

const {
    getAllProductsMap,
    CPP_PRODUCT_CODE,
    CP_PRODUCT_CODE,
    CR_PRODUCT_CODE,
    GL_PRODUCT_CODE,
    IM_PRODUCT_CODE
} = WniProductsUtil;


const themeName = ConfigUtil.getThemeName();

const isMaxDate = (date1, date2) => {
    return moment(date1).isAfter(date2) || moment(date1).isSame(date2);
}; 

function useProductsData(useAuthenticationData) {
    const translator = useTranslator();
    const {
        authUserData: {
            businessData_Ext: {
                productLookup_Ext: productMapsForPC = {},
                productLookup: productMapsForPortal= {},
                systemDate
            }
        },
    } = useAuthenticationData || useAuthentication();
    const localDate = LocalDateUtil.today();

    const productLookupForPC = _.mapValues(productMapsForPC, (value, key) => {
        if(_.includes([CP_PRODUCT_CODE, CR_PRODUCT_CODE], key)) {
            const cppLookupData = productMapsForPC[CPP_PRODUCT_CODE] || [];
            return cppLookupData.map((v) => {
                return {...v, productCode: key}
            })
        };
        return value
    })

    const productLookupForPortal = _.mapValues(productMapsForPortal, (value, key) => {
        if(_.includes([CP_PRODUCT_CODE, CR_PRODUCT_CODE], key)) {
            const cppLookupData = productMapsForPortal[CPP_PRODUCT_CODE] || [];
            return cppLookupData.map((v) => {
                return {...v, productCode: key}
            })
        };
        return value
    });

    // compare systemDate with LocalDate to get the lastest date
    const newLocalDate = isMaxDate(systemDate, localDate) ? systemDate : localDate;

    const retrieveProductAvailableStates = (productCode) => {
        if(!WniProductsUtil.isCLProduct(productCode)) {
            return aoStates;
        }

        let itemProductData = productLookupForPortal[productCode] || [];

        if(_.includes([GL_PRODUCT_CODE, IM_PRODUCT_CODE], productCode)) {
            const itemProductDataForCPP = (productLookupForPortal[CPP_PRODUCT_CODE] || []).filter((v) => v.availability === AVAILABLE_CODE);
            itemProductData = [...itemProductData, ...itemProductDataForCPP];
        };

        const productEnableStateMaps = itemProductData.filter((v) => v.availability === AVAILABLE_CODE && isMaxDate(newLocalDate, v.creationDate)).map((item) => item.state);
        const productEnableStates = _.uniq(productEnableStateMaps);
        // ---- NEW: theme-based state allowlist ----
        // If themeName is one of the 3 brands, apply that allowlist.
        // Otherwise (lower env / unknown), do not restrict beyond productEnableStates.
        const allowedStatesForTheme = _.get(companyGroups, themeName);

        if (_.isArray(allowedStatesForTheme) && !_.isEmpty(allowedStatesForTheme)) {
              return productEnableStates.filter((state) => _.includes(allowedStatesForTheme, state));
        }

       return productEnableStates;
    };

    const allProducts = getAllProductsMap();
    
    // POI-26606: personal lines licensed state about user
    /** POI-41751:  for new requirement  Remove Licensed state check         */
    // const personalLinesLicenseState = licenseInformation
    //     // .filter((item) => item.linesOfAuthority === 'paline')
    //     .map((v) => v.state);

    const renderProductLookup = (productCode) => {
        const itemProductData = productLookupForPC[productCode] || [];
        const newProduct = [];
        const availableStateForProduct = retrieveProductAvailableStates(productCode);
        // following the PC, if product is available but not has state, this product is available for all state
        if (itemProductData.length === 1 && !itemProductData[0].state) {
            availableStateForProduct.forEach((item) => {
                newProduct.push({...itemProductData[0], state: item})
            });
            return newProduct;
        };
        return itemProductData.filter((item) => availableStateForProduct.includes(item.state));
    };

    /**
     * 
     * @returns {object} calculate the availability of productes both in PC and PE
     */
    const renderProducts = () => {
        const newProductLookupForPC = _.mapValues(productLookupForPC, (value, key) => renderProductLookup(key));
        
        const newProducts = _.mapValues(newProductLookupForPC, (itemProductForPC, productCode) => {
            const itemForPortal = productLookupForPortal[productCode];

            if(itemForPortal) {
                const newProductItem = _.map(itemProductForPC, (stateItemForPC) => {
                    const stateItemForPortal = _.find(itemForPortal, (v) => v.state === stateItemForPC.state);

                    if(stateItemForPortal) {
                        const productAvailableForPortal = stateItemForPortal.availability === AVAILABLE_CODE;
                        let available = stateItemForPC.availability === AVAILABLE_CODE && productAvailableForPortal;

                        if(_.includes([GL_PRODUCT_CODE, IM_PRODUCT_CODE], productCode)) { // if product is a line included in CPP, it is necessary to check whether CPP is available
                            const cppProductFormPC = productLookupForPC[CPP_PRODUCT_CODE];
                            const cppProductStateFromPC = cppProductFormPC?.find((v) => v.state === stateItemForPortal.state);

                            if(cppProductStateFromPC) {
                                available = (stateItemForPC.availability === AVAILABLE_CODE || cppProductStateFromPC.availability === AVAILABLE_CODE) && productAvailableForPortal;
                            }
                        }

                        const startEffectiveDate = isMaxDate(stateItemForPC.startEffectiveDate, stateItemForPortal.startEffectiveDate) ? stateItemForPC.startEffectiveDate : stateItemForPortal.startEffectiveDate
                        return {
                            ...stateItemForPC,
                            availability: available ? AVAILABLE_CODE : UNAVAILABLE_CODE,
                            creationDate: stateItemForPortal.creationDate, // from portal management configurations
                            startEffectiveDate: startEffectiveDate,
                            effectiveDate: startEffectiveDate, // from portal management configurations
                        }
                    }
                    return stateItemForPC
                });
                return newProductItem;
            }
            return itemProductForPC
        });

        return newProducts;
    };


    // product abailable is true or false according 'Available' state min startEffectiveDate
    const renderProductsMap = () => {
        const newALllProducts = allProducts.map((product) => {
            const newProductLookup = renderProducts();
            const productStateRules = _.get(newProductLookup, product.code, []);
            // get the 'Available' state in productStateRules
            let availableStatesEnabled = productStateRules.filter((item) => item.availability === AVAILABLE_CODE);

            if(_.includes([GL_PRODUCT_CODE, IM_PRODUCT_CODE], product.code)) { // if product is a line included in CPP, it is necessary to check whether CPP is available
                const availableStateForCPP = _.get(newProductLookup, CPP_PRODUCT_CODE, []).filter((item) => item.availability === AVAILABLE_CODE);
                const generateAvailableStateForCPP = _.clone(availableStateForCPP).map((item) => {
                    const matchAvailableStateData = _.find(availableStatesEnabled, (v) => v.state === item.state);
                    if(matchAvailableStateData) {
                        return {
                            ...item,
                            productCode: product.code,
                            creationDate: isMaxDate(item.creationDate, matchAvailableStateData.creationDate) ? matchAvailableStateData.creationDate : item.creationDate,
                            effectiveDate: isMaxDate(item.effectiveDate, matchAvailableStateData.effectiveDate) ? matchAvailableStateData.effectiveDate : item.effectiveDate,
                        }
                    };

                    return {
                        ...item,
                        productCode: product.code
                    }
                })

                availableStatesEnabled = _.merge(availableStatesEnabled, generateAvailableStateForCPP);
            }

            if (!_.isEmpty(availableStatesEnabled)) {
                // get availableStates min startEffectiveDate
                const minStartEffectiveDate = availableStatesEnabled.reduce((a, b) =>!isMaxDate(a.startEffectiveDate, b.startEffectiveDate) ? a : b).startEffectiveDate;
                // whether local date is greater than the min startEffectiveDate
                const timeEnabled = isMaxDate(newLocalDate, minStartEffectiveDate);

                return {
                    ...product,
                    name: product.productName,
                    tooltip: translator(product.tooltip),
                    available: true,
                    startEffectiveDate: minStartEffectiveDate,
                    minDate: timeEnabled ? newLocalDate : minStartEffectiveDate,
                    productRules: availableStatesEnabled,
                    productDateMap: productLookupForPortal[product.code]
                };
            }
            return {
                ...product,
                name: product.productName,
                tooltip: translator(product.tooltip),
                available: false,
                productRules: availableStatesEnabled,
                productDateMap: productLookupForPortal[product.code]
            };
        });

        return newALllProducts;
    };
    const generateAllProducts = renderProductsMap();

    /**
     *
     * @returns {array} available products
     */
    const getProductsMap = (accountType) => {
        const availableProducts = generateAllProducts.filter((product) => product.available);
        // filter by account type 
        if (accountType) {
            return availableProducts.filter((item) => item.accountType === accountType);
        }

        return availableProducts
    };

    /**
     *
     * @param {string} code product code
     * @returns {object} return item product
     */

    const getProductItem = (code) => {
        return _.find(generateAllProducts, (item) => item.code === code);
    };
    /**
     *
     * @param {string} code
     * @returns {string|object}
     */

    const getProductName = (code) => {
        if (!code) {
            return '';
        }
        const productItem = _.find(allProducts, (item) => item.code === code);
        return translator(_.get(productItem, 'name'));
    };

    /**
     *
     * @param {string} code product code
     * @returns {boolean} product abailable or not
     */
    const getProductEnabled = (code) => {
        const productItem = getProductItem(code);
        return productItem.available;
    };

    /**
     *
     * @param {string} code product code
     * @param {object|string} AOEffectiveDate date from ao
     * @returns {object} return effective date
     */
    const getProductEffectiveDate = (code, AOEffectiveDate) => {
        const productItem = getProductItem(code);
        const productMinDate = productItem.minDate;
        if (!AOEffectiveDate) {
            return productMinDate;
        }

        const AOEffectiveDateFormat = WniDateUtil.getDateObj(AOEffectiveDate);
        const maxDateWithAOAndMin = isMaxDate(AOEffectiveDateFormat, productMinDate) ? AOEffectiveDateFormat : productMinDate;
        return maxDateWithAOAndMin
    };

    const getProductMinDate = (code, policyState) => {
        const productItem = getProductItem(code);
        const { productRules } = productItem;

        const filterProductWithState = _.find(productRules, (item) => { return item.state === policyState} );
        if(policyState && filterProductWithState) {
            return isMaxDate(filterProductWithState.startEffectiveDate, newLocalDate) ? filterProductWithState.startEffectiveDate : newLocalDate
        }
        
        return productItem.minDate;
    };


    /**
     * for new requirement, not recommended to use it again.
     * as it has been used in some places before, it is kept and cannot be deleted.
     */
    const getProductStartEffectiveDate = (code, policyState) => {
        const productItem = getProductItem(code);
        const { productRules } = productItem;

        const filterProductWithState = _.find(productRules, (item) => { return item.state === policyState} );
        if(policyState && filterProductWithState) {
            return filterProductWithState.startEffectiveDate
        }
        
        return productItem.minDate;
    };

    /**
     * for new requirement, not recommended to use it again.
     * as it has been used in some places before, it is kept and cannot be deleted.
     */
    const getProductAvailableStates = (code) => {
        const productItem = getProductItem(code);
        const { productRules = [] } = productItem;
        return productRules.map((a) => a.state);
    };

    const getAvailableProducts = (accountType, state, withCPP = false) => {
        // filter by account type 
        // if company, should not show CPP single
        const productsFilterByAccount = getProductsMap(accountType)
        let accountProducts = productsFilterByAccount;
        if(!withCPP) {
            accountProducts = productsFilterByAccount.filter((item) => item.code !== CPP_PRODUCT_CODE);
        }
        
        // if have not select state, will be available when the min creation date 
        if(!state) {
            const fetchProducts = accountProducts.map((item) => {
                const { productDateMap } = item;
                if(!productDateMap) {
                    return item;
                }
                const availableStateForProduct = retrieveProductAvailableStates(item.code);
                const filterAvailableStateForProducts = productDateMap.filter((v) => availableStateForProduct.includes(v.state));
                let isAvailable = true;
                if(_.isEmpty(filterAvailableStateForProducts)) {
                    isAvailable = false
                } else {
                    const minCreationDate = filterAvailableStateForProducts.reduce((a, b) =>!isMaxDate(a.creationDate, b.creationDate) ? a : b).creationDate;
                    isAvailable = isMaxDate(newLocalDate, minCreationDate);
                }
                
                return {
                    ...item,
                    available: isAvailable
                }
            })

            return fetchProducts.filter((product) => product.available);
        }
        // ---------------------------------------

        // if has state, filter the available product for selected state
        const fetchProductsForState = accountProducts.map((item) => {
            const { productDateMap } = item;
            if(!productDateMap) {
                return item;
            }
            const dateForState = productDateMap.find((eachData) => eachData.state === state);
            // if select state without dateMap, will be available whne the min creation date
            if(!dateForState) {
                const availableProductDateMap = productDateMap.filter((v) => v.availability === AVAILABLE_CODE);
                
                let isAvailable = true;
                if(_.isEmpty(availableProductDateMap)) {
                    isAvailable = false;
                } else {
                    const minCreationDate = availableProductDateMap.reduce((a, b) =>!isMaxDate(a.creationDate, b.creationDate) ? a : b).creationDate;
                    isAvailable = isMaxDate(newLocalDate, minCreationDate);
                }
               
                return {
                    ...item,
                    available: isAvailable
                }
            }

            const { creationDate } = dateForState;

            let isAfterCreationDate = true
            if(creationDate) {
                isAfterCreationDate = isMaxDate(newLocalDate, creationDate);
            }
            
            return {
                ...item,
                available: isAfterCreationDate
            }
        });
        
        return fetchProductsForState.filter((product) => product.available)
    };

    const getAvailableEffectiveDate = (productCode, baseState, AOEffectiveDate) => {
        const productItem = getProductItem(productCode);
        const { productDateMap } = productItem || {};
        // if not find data, return minDate
        if(!productDateMap){
            // minDate: productlookup form pc, and this is the minDate of different state startEffectiveDate
            return productItem.minDate
        }
        const dateForState = productDateMap.find((eachData) => eachData.state === baseState);
        if(!dateForState) {
            return newLocalDate
        }
        const { startEffectiveDate } = dateForState;
        const availableEffectiveDate = isMaxDate(startEffectiveDate, newLocalDate) ? startEffectiveDate : newLocalDate;
        if(!AOEffectiveDate) {
            return availableEffectiveDate
        }
        const AOEffectiveDateFormat = WniDateUtil.getDateObj(AOEffectiveDate);
        return isMaxDate(availableEffectiveDate, AOEffectiveDateFormat) ? availableEffectiveDate : AOEffectiveDateFormat;
    };

    const getAvailablePolicyState = (productCode, policyEffectiveDate) => {
        const availableStateForProduct = retrieveProductAvailableStates(productCode);
        if(!policyEffectiveDate) {
            return availableStateForProduct
        };
       
        const productItem = getProductItem(productCode);
        const { productDateMap = [] } = productItem || {};

        const availableState = [];
        productDateMap.forEach((item) => {
            const { state, startEffectiveDate } = item;
            if(isMaxDate(policyEffectiveDate, startEffectiveDate)) {
                availableState.push(state);
            }
        });
        return _.intersection(availableState, availableStateForProduct);
    };

    const getProductVisibleForState = (productCode, baseState) => {
        /** find available or not in PC Product Model ---satrt */ 
        const productItem = getProductItem(productCode);
        if(!productItem) {
            return false
        }
        const { productDateMap } = productItem;

        if(!productDateMap) {
            return true;
        }
        
        const filterProductWithState = _.find(productDateMap, (item) => item.state === baseState );
        if (( _.get(filterProductWithState, 'availability') !== AVAILABLE_CODE)) {
            return false;
        };
        /** find available or not in PC Product Model ---end */ 

        /** find available or not with the localDate ---satrt */ 
        const dateForState = productDateMap.find((eachData) => eachData.state === baseState);
        if(!dateForState) {
            return false
        }
        const { creationDate } = dateForState;
        return isMaxDate(newLocalDate, creationDate)

    };

    const getDefaultBaseState = (productCode, accountAddress) => {
        const availableStateForProduct = retrieveProductAvailableStates(productCode);
        const accountState = accountAddress.state;
        if(_.includes(availableStateForProduct, accountState)) {
            return accountState
        }

        const availableStatesWithoutAK = availableStateForProduct.filter((v) => v !== 'AK');
        if(accountState !== 'AK' && !_.isEmpty(availableStatesWithoutAK)) {
            
            return availableStatesWithoutAK[0]
        }
        return availableStateForProduct[0]
    };

    /**
     * check if policy change and cancel policy enable or not.
     * @param {string} productCode 
     * @param {string} policyState 
     * @returns
     */
    const isChangeCancelEnable = (productCode, policyState) => {
        const productItem = getProductItem(productCode);
        const { productDateMap } = productItem || {};
        if(!productDateMap) {
            return true;
        }

        const filterProductWithState = _.find(productDateMap, (item) => item.state === policyState);
        return filterProductWithState?.enableChangeCancel
        
    };

    /**
     * for new requirement, not recommended to use it again.
     * as it has been used in some places before, it is kept and cannot be deleted.
     */
    const getProductsMinDate = (productCode, baseState) => {
        const { productsDateMaps } = WniProductsDateUtil;
        const productDateMap = productsDateMaps[productCode];
        // if not find data, return minDate
        if(!productDateMap){
            const productItem = getProductItem(productCode);
            return productItem.minDate
        }
        const dateForState = productDateMap.find((eachData) => eachData.state === baseState);

        return dateForState.rateBookDate;
    };

    return {
        getProductsMap,
        getProductItem,
        getProductName,
        getProductEnabled,
        getProductEffectiveDate,
        getProductMinDate,
        /** not recommended -- START */
        getProductAvailableStates,
        getProductStartEffectiveDate,
        getProductsMinDate,
        /** not recommended -- END */

        // for go live story
        getAvailableProducts,
        getAvailableEffectiveDate,
        getAvailablePolicyState,
        getProductVisibleForState,
        isChangeCancelEnable,
        getDefaultBaseState,

        
    };
}

export default useProductsData;
